import React, {useEffect, useState, useRef} from "react";
import ChartViewer from "./ChartViewer";
import {useCubeQuery} from "../../../utils/hooks/useCubeQuery";
import {Select} from "antd";
import {useCurrentUser} from "../../../utils/CurrentUserContext";
import {formatDateRangeForQuery} from "../../../utils/date-utils";

const TimeGraph = ({aggPublisherFilters, dateRange, campaignId, cubejsApi}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [metric, setMetric] = useState("tdImpressions");
  const graphContainerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(200);
  const {user} = useCurrentUser();

  const options = [
    {label: "Reads", value: "reads"},
    {label: "Impressions", value: "tdImpressions"},
    {label: "Clickouts", value: "clickouts"},
    {label: "Conversions", value: "conversions"},
    {label: "Reads per 100 Imps", value: "readImps"},
    {label: "Average Read Time", value: "average_readtime"},
    ...(user.company.type === "PUBLISHER"
      ? [
        {label: "eCPM", value: "eCpm"},
        {label: "Total Revenue", value: "publisherRevenue"},
      ]
      : [{label: "Total Cost", value: "totalCost"}]),
  ];

  const getMetricLabel = (metric) => {
    const selectedOption = options.find(option => option.value === metric);
    return selectedOption ? selectedOption.label : metric;
  };

  const {resultSet: aggData, error: aggError, isLoading: aggIsLoading} = useCubeQuery(
    {
      dimensions: ["aggregateTdLevel.campaignName"],
      measures: [`aggregateTdLevel.${metric}`],
      order: {
        "aggregateTdLevel.date": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.campaignId",
          operator: "equals",
          values: [campaignId],
        },
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals",
          values: ["true"],
        },
        ...aggPublisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          granularity: "day",
          dateRange: typeof dateRange[0] === 'string' ? dateRange : formatDateRangeForQuery(dateRange),
        },
      ],
    },
    {cubejsApi}
  );

  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 300); // Delay for smooth transition
    return () => clearTimeout(timer);
  }, [metric]);

  // To avoid bumping the page down
  useEffect(() => {
    if (graphContainerRef.current) {
      setContainerHeight(graphContainerRef.current.offsetHeight || 300);
    }
  }, [aggData, isTransitioning]);

  if (aggError) {
    return <div>Error: {aggError.toString()}</div>;
  }

  const resultSet = {
    chartPivot: (pivotConfig) =>
      aggData?.tablePivot(pivotConfig).map((row) => ({
        x: row["aggregateTdLevel.date.day"],
        y: row[`aggregateTdLevel.${metric}`],
      })) || [],
    series: (pivotConfig) => [
      {
        title: getMetricLabel(metric),
        series:
          aggData?.tablePivot(pivotConfig).map((row) => ({
            value: row[`aggregateTdLevel.${metric}`],
          })) || [],
      },
    ],
  };

  const pivotConfig = {
    x: ["aggregateTdLevel.date.day"],
    y: [`aggregateTdLevel.${metric}`],
  };

  return (
    <>

      <div
        ref={graphContainerRef}
        className="mt-4 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow p-4 md:grid-cols-6 md:divide-y-0 md:divide-x"
      >
        <Select
          className="mb-4"
          defaultValue="reads"
          value={metric}
          onChange={setMetric}
          options={options}

        />
        <h2 className="col-span-full text-xl font-bold mb-4 text-gray-700 first-letter:uppercase">
          {`${getMetricLabel(metric)} Over Time`}
        </h2>
        <div className="col-span-full">
          {aggIsLoading || isTransitioning ? (
            <div
              style={{minHeight: `${containerHeight}px`}} 
              className="flex justify-center items-center text-gray-500"
            >
              Loading graph...
            </div>
          ) : (
            <ChartViewer resultSet={resultSet} pivotConfig={pivotConfig} chartType="line"/>
          )}
        </div>
      </div>
    </>
  );
};

export default TimeGraph;
